/**
 * Archivo de inicialización de objetos para el módulo de cotizador
 *
 * Este archivo centraliza la creación de objetos iniciales utilizados en el cotizador
 * y componentes relacionados, reduciendo la duplicación de código y facilitando el mantenimiento.
 *
 * @module CotizadorInitializers
 */

import { Ciudades } from '../../../Models/ciudades';
import { DatosGenerales } from '../../../Models/datosGenerales';
import { DestinosModels } from '../../../Models/destinos';
import { ExceptionRequestCotizacion } from '../../../Models/exceptionRequestCotizacion';

/**
 * Crea un objeto Ciudades vacío con la estructura inicial
 *
 * @returns {Ciudades} Objeto Ciudades inicializado
 */
export function createEmptyCiudades(): Ciudades {
  return {
    data: {
      cities: [
        {
          label: '',
          value: '',
        },
      ],
    },
  };
}

/**
 * Crea un objeto DestinosModels vacío con la estructura inicial
 *
 * @returns {DestinosModels} Objeto DestinosModels inicializado
 */
export function createEmptyDestinos(): DestinosModels {
  return {
    data: {
      circulation_zones: [
        {
          circulation_zone: '',
          is_invalid: false,
          label: '',
          value: 0,
        },
      ],
    },
  };
}

/**
 * Crea un objeto DestinosModels simple (sin datos iniciales)
 * Usado principalmente en formularios
 *
 * @returns {DestinosModels} Objeto DestinosModels con array vacío
 */
export function createSimpleDestinos(): DestinosModels {
  return {
    data: {
      circulation_zones: [],
    },
  };
}

/**
 * Crea un objeto DatosGenerales vacío con la estructura inicial completa
 * Incluye la estructura de Lead y LeadResponse con todos sus campos inicializados
 *
 * @returns {DatosGenerales} Objeto DatosGenerales inicializado
 */
export function createEmptyDatosGenerales(): DatosGenerales {
  return {
    Lead: {
      assessor_code: 0,
      plate: '',
      confirm_plate: '',
      circulation_zone_id: '',
      use_type: '',
      accept_tos: false,
      name: '',
      mobile_phone: '',
    },
    LeadResponse: {
      additionalInfo: {
        isPrenda: '',
        prendario: '',
      },
      data: {
        Id: '',
        lead: {
          accept_tos: false,
          mobile_phone: '',
          owner_borndate: '',
        },
        vehicle: {
          Prenda: '',
          brand: '',
          circulation_zone_id: '',
          cylinder: '',
          model: '',
          owner: {
            document: {
              number: '',
              type: '',
            },
            firstnames: '',
            lastnames: '',
          },
          plate: '',
          use_type: '',
        },
      },
      error: 0,
      errorCode: '',
      message: '',
    },
  };
}

/**
 * Crea un objeto ExceptionRequestCotizacion vacío con la estructura inicial
 * Utilizado para el envío de excepciones desde el cotizador
 *
 * @returns {ExceptionRequestCotizacion} Objeto ExceptionRequestCotizacion inicializado
 */
export function createEmptyExceptionRequest(): ExceptionRequestCotizacion {
  return {
    required_status: '',
    percentege_lead: 0,
    plate: '',
    mobile_phone: '',
    product: '',
    name: '',
  };
}

/**
 * Crea un objeto DatosGenerales pre-poblado con datos parciales
 * Útil para escenarios de retoma de sesión o pre-llenado de formularios
 *
 * @param {Partial<DatosGenerales>} partialData - Datos parciales para pre-poblar
 * @returns {DatosGenerales} Objeto DatosGenerales con datos mezclados
 */
export function createDatosGeneralesWithData(
  partialData: Partial<DatosGenerales>
): DatosGenerales {
  const empty = createEmptyDatosGenerales();

  return {
    Lead: {
      ...empty.Lead,
      ...(partialData.Lead || {}),
    },
    LeadResponse: {
      ...empty.LeadResponse,
      ...(partialData.LeadResponse || {}),
      data: {
        ...empty.LeadResponse.data,
        ...(partialData.LeadResponse?.data || {}),
      },
    },
  };
}
